/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2015-2017, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

angular.module('bulbApp.pageViewModule')
  .directive('bulbPageView', [
    '$',
    '$log',
    '$state',
    '$timeout',
    '$window',
    'Foundation',
    'Restangular',
    'BulbBusyService',
    'BulbErrorService',
    'BulbPresentationService',
    'BulbRecommendService',
    'BulbUnitService',
    'SerialSaveService',
    'UnitStateStore',
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    bulbPageView,
  ]);

function bulbPageView(
  $,
  $log,
  $state,
  $timeout,
  $window,
  Foundation,
  Restangular,
  BulbBusyService,
  BulbErrorService,
  BulbPresentationService,
  BulbRecommendService,
  BulbUnitService,
  SerialSaveService,
  UnitStateStore,
) {
  return {
    restrict: 'E',
    templateUrl: 'ajs/components/pageview/pageview.html',
    controller: 'PageViewController as pageViewCtrl',
    bindToController: {
      unit: '=',
    },
    scope: true,
    link(scope, element, attrs) {
      const { pageViewCtrl } = scope;

      if (!angular.isString(attrs.unit)) {
        throw new Error('bulb-auto-save requires a unit attribute');
      }
      pageViewCtrl.unit = scope.$eval(attrs.unit);

      // Handle the cover image layout
      // TODO CLEAN : this cover image code is virtually identical to the cover image code in
      // compositeview-directive.js and needs to be pulled out into a directive of its own so we don't have
      // duplicate code to maintain.
      const jqWindow = $($window);
      let minCoverImageTotalHeight = 420;

      if (!Foundation.utils.is_large_up()) { // jshint ignore:line
        // NOTE : this represents the utilization of 33% of the browser height on mobile devices.  It is
        // duplicated in the scss for 'bulb-has-cover-image' and in imeageeditor-directive.js, so changing
        // it here should change it there as well.
        // FUTURE: Should consider a mechanism to share duplicated information between SASS and JS.
        minCoverImageTotalHeight = jqWindow.height() / 3;
      }

      // Lay out the cover image, based on its width and height.
      const updateHeaderSizes = function updateHeaderSizes() {
        // This represents a buffer so that the header banner doesn't overlay the 'edit image' button for the
        // cover image.  It is paralleled by css so modifications should be made in both places.
        // FUTURE : use a mechanism to share duplicated information between SASS and JS.
        const editImageBuffer = Foundation.utils.is_medium_up() ? 62 : 52; // jshint ignore:line

        const { coverImage } = pageViewCtrl.page.unit;
        if (coverImage) {
          const coverImageElement = $('.bulb-cover-image');
          const presentation = BulbPresentationService.isFullScreen();
          const windowWidth = presentation ? BulbPresentationService.getWidth()
            : jqWindow.width();
          let scale;
          // default values for width, height, left, and top are '',
          // which jQuery.css interprets as "remove style".
          let width = '';
          let height = '';
          let left = '';
          let top = '';
          // eslint-disable-next-line no-param-reassign
          scope.style = '';

          const bannerHeight = $('.bulb-cover-image-banner').height() + editImageBuffer;
          let coverImageTotalHeight = Math.max(minCoverImageTotalHeight, bannerHeight);

          if (presentation) {
            coverImageTotalHeight *= BulbPresentationService.getWidth() / parseInt(jqWindow.width(), 10);
            $('.bulb-has-cover-image').css('min-height', coverImageTotalHeight);
          }

          const imageAspectRatio = coverImage.width / coverImage.height;
          const headerAspectRatio = windowWidth / coverImageTotalHeight;

          if (imageAspectRatio > headerAspectRatio) {
            // image wider than header: scale image height to header height, and center horizontally
            height = coverImageTotalHeight;
            scale = height / coverImage.height;
            left = (windowWidth - scale * coverImage.width) / 2;
          } else {
            // header wider than image: scale image width to header width, and center vertically
            width = windowWidth;
            scale = width / coverImage.width;
            top = (coverImageTotalHeight - scale * coverImage.height) / 2;
          }

          const style = {
            width,
            height,
            top,
            left,
          };

          $log.debug(`bulbCompositeView: setCoverImage: window width): ${windowWidth} style: ${
            JSON.stringify(style, null, 2)}`);
          coverImageElement.css(style);
          // eslint-disable-next-line no-param-reassign
          scope.style = style;
          $('.bulb-unit-header').css({
            height: '',
          });
        } else {
          $('.bulb-unit-header').css({
            height: '',
          });
        }
      };

      const onResize = function onResize() {
        if (pageViewCtrl.page.unit && pageViewCtrl.page.unit.coverImage) {
          updateHeaderSizes();
        }
      };

      // Rescale cover image on window resize.
      jqWindow.on('resize', onResize);

      // Watch for height changes of the header banner, and adjust the cover image when they happen.
      scope.$watch(() => $('.bulb-cover-image-banner').height(), () => {
        updateHeaderSizes();
      });

      // If coverImage changes, or mode is no longer 'editor', rescale the cover image.
      scope.$watchGroup([
        'pageViewCtrl.mode',
        'pageViewCtrl.page.unit.coverImage.url',
        'pageViewCtrl.page.unit.coverImage.width',
        'pageViewCtrl.page.unit.coverImage.height',
      ], (newValues) => {
        const mode = newValues[0];

        if (mode !== 'editor') {
          // Either something about the image changed, or mode is no longer 'editor': rescale the image.
          // Running this after the current digest cycle so the header ng-classes have a chance to update
          // before we recalculate sizes.
          $timeout(updateHeaderSizes);
        }
      });

      let coverImageBeingCropped;

      pageViewCtrl.cropStart = function cropStart(coverImage) {
        pageViewCtrl.coverImageMode = 'editor';
        $timeout(() => {
          const imageElement = element.find('.cover-image-crop');
          imageElement.attr('src', coverImage.url);

          // Calculate the minimum size of the cropper so that the resulting image is at least 835x175.
          // TODO CLEAN : get 835 and 260 from configuration or asset definitions
          const container = element.find('.bulb-alt-cover-image-editor');
          const containerWidth = container.width();
          const containerScale = containerWidth / 835;
          const imageScale = coverImage.width / 835;

          const minWidth = (835 / imageScale) * containerScale;
          const minHeight = (260 / imageScale) * containerScale;

          /** @function imageElement.cropper */
          imageElement.cropper({
            viewMode: 3,
            checkCrossOrigin: false,
            movable: false,
            rotatable: false,
            scalable: false,
            zoomable: false,
            zoomOnTouch: false,
            zoomOnWheel: false,
            minCropBoxWidth: minWidth,
            minCropBoxHeight: minHeight,
            data: coverImage.cropRectangle,
          });

          coverImageBeingCropped = coverImage;
        });
      };

      pageViewCtrl.cropSave = function cropSave() {
        const imageElement = element.find('.cover-image-crop');
        const cropData = imageElement.cropper('getData', 'rounded');

        const uploadAsset = {
          assetId: coverImageBeingCropped.id,
          assetDefinitionsKey: 'coverImage',
          cropRectangle: {
            x: cropData.x,
            y: cropData.y,
            width: cropData.width,
            height: cropData.height,
          },
        };

        coverImageBeingCropped = undefined;

        SerialSaveService.save({
          showBusy: true,
          save: () => Restangular.one('units', pageViewCtrl.page.unit.id)
            .customPOST(uploadAsset, 'coverimage')
            .then((pageUnit) => {
              const updatedUnit = pageUnit.plain();
              BulbUnitService.setCurrentUnit(updatedUnit);

              // FUTURE: some more general way of updating interested components?
              BulbRecommendService.updateRecommend(updatedUnit);

              // Update share status to reflect that the page has been edited:
              const ShareTransition = UnitStateStore.getShareTransitionEnum();
              UnitStateStore.transitionShareStatus(ShareTransition.EDIT);
            }),
        }).finally(() => {
          pageViewCtrl.cropStop();
          $state.reload();
        });
      };

      pageViewCtrl.cropStop = function cropStop() {
        const imageElement = element.find('.cover-image-crop');
        imageElement.cropper('destroy');
        pageViewCtrl.coverImageMode = 'default';
      };
    },
  };
}
